import {
  Badge,
  type BadgeProps,
  Box,
  Button,
  HStack,
  Text,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import {
  BillingPlanBudgetCacheStatusEnum,
  BillingPlanBudgetItemInvoiceTypeEnum,
  BillingPlanCacheOrderByColumnEnum,
  type InvoicingPlanAdvancedListFragment,
} from "@src/__generated__/graphql";
import { InvoicingPlanAutomatedListStore } from "@src/components/modules/invoicing-plan/automated-list/store";
import { InvoicingPlanLinks } from "@src/components/modules/invoicing-plan/components/links";
import { DeliveredStatusRenderer } from "@src/components/modules/invoicing-plan/List/components/columns";
import {
  ACTIONS_COLUMN_ID,
  Avatar,
  type BodyColumnDef,
  BrandLogo,
  ButtonLink,
  DataTable,
  formatDate,
  Link,
  LoadingOverlay,
  UserTooltip,
} from "@src/components/ui-kit";
import { Pagination } from "@src/components/ui-kit/table/pagination";
import { PeriodPicker } from "@src/components/widgets/PeriodPicker";
import { FiltersPanelRoot } from "@src/utils/components/filters/components/filters-panel";
import { FiltersPanelFilters } from "@src/utils/components/filters/components/filters-panel-filters";
import { FiltersPanelSearch } from "@src/utils/components/filters/components/filters-panel-search";
import { currency } from "@src/utils/formatters";
import { useStore, useUpdateURI } from "@src/utils/hooks";
import { observer, useLocalObservable } from "mobx-react-lite";
import { useMemo } from "react";

export const InvoicingPlanAutomatedList = observer(
  function InvoicingPlanAutomatedList() {
    const appStore = useStore();
    const store = useLocalObservable(
      () => new InvoicingPlanAutomatedListStore(appStore),
    );

    useUpdateURI(store.searchParamsExtended);
    const columns = useMemo<
      BodyColumnDef<
        InvoicingPlanAdvancedListFragment,
        BillingPlanCacheOrderByColumnEnum
      >[]
    >(
      () => [
        {
          id: "project-manager",
          meta: { label: t`PM` },
          header: t`PM`,
          cell: ({ row }) => {
            const { projectManager } = row.original.project;
            if (!projectManager) return null;

            return (
              <UserTooltip user={projectManager}>
                <Avatar
                  src={projectManager.image?.urls.thumbnail}
                  name={projectManager.full_name}
                  colorScheme={projectManager.profile?.hex_color}
                />
              </UserTooltip>
            );
          },
        },
        {
          id: "brand",
          meta: { label: t`Brand` },
          header: t`Brand`,
          cell: ({ row }) => {
            if (!row.original.brand) return null;

            return (
              <Tooltip
                label={row.original.brand.name}
                placement="top"
                shouldWrapChildren
              >
                <ButtonLink
                  variant="plain"
                  href={{
                    pathname: "/brands/detail",
                    query: { id: row.original.brand.id },
                  }}
                  mr="2"
                  target="_blank"
                >
                  <BrandLogo
                    src={row.original.brand?.image?.urls?.thumbnail}
                    name={row.original.brand?.name}
                  />
                </ButtonLink>
              </Tooltip>
            );
          },
        },
        {
          id: "project",
          meta: { label: t`Project` },
          header: t`Project`,
          cell: ({ row }) => (
            <Tooltip
              label={row.original.project.title}
              placement="top"
              shouldWrapChildren
            >
              <Link
                href={{
                  pathname: "/projects/detail",
                  query: { id: row.original.project.id },
                }}
                target="_blank"
              >
                <Box>{row.original.project.code}</Box>
                <Box>{row.original.project.title}</Box>
              </Link>
            </Tooltip>
          ),
        },
        {
          id: "planned_amount",
          meta: { label: t`Planned amount`, align: "right" },
          header: t`Planned amount`,
          orderBy: BillingPlanCacheOrderByColumnEnum.PlannedAmount,
          cell: ({ row }) =>
            currency.formatByWorkspace(row.original.planned_amount),
        },
        {
          id: "invoiced_amount",
          meta: { label: t`Invoiced amount`, align: "right" },
          header: t`Invoiced amount`,
          orderBy: BillingPlanCacheOrderByColumnEnum.InvoicedAmount,
          cell: ({ row }) =>
            currency.formatByWorkspace(row.original.invoiced_amount),
        },
        {
          id: "issue_date",
          meta: { label: t`Issue date`, align: "center" },
          header: t`Issue date`,
          orderBy: BillingPlanCacheOrderByColumnEnum.IssueDate,
          cell: ({ row }) => formatDate(new Date(row.original.issue_date)),
        },
        {
          id: "date_of_supply",
          meta: { label: t`Date of supply`, align: "center" },
          header: t`Date of supply`,
          orderBy: BillingPlanCacheOrderByColumnEnum.DateOfSupply,
          cell: ({ row }) => formatDate(new Date(row.original.date_of_supply)),
        },
        {
          id: "due_date",
          meta: { label: t`Due date`, align: "center" },
          header: t`Due date`,
          orderBy: BillingPlanCacheOrderByColumnEnum.DueDate,
          cell: ({ row }) => formatDate(new Date(row.original.due_date)),
        },
        {
          id: "type",
          meta: { label: t`Type` },
          header: t`Type`,
          cell: ({ row }) => {
            switch (row.original.type) {
              case BillingPlanBudgetItemInvoiceTypeEnum.OutgoingInvoice:
                return t`Outgoing invoice`;
              case BillingPlanBudgetItemInvoiceTypeEnum.OutgoingProformaInvoice:
                return t`Proforma invoice`;
              default:
                return t`Request for invoicing`;
            }
          },
        },
        {
          id: "invoice_no",
          meta: { label: t`Invoice no` },
          header: t`Invoice no`,
          cell: ({ row }) => {
            if (row.original.request_for_invoicing_id) {
              return (
                <Link
                  href={{
                    pathname: "/invoices/for-invoicing/edit",
                    query: { id: row.original.request_for_invoicing_id },
                  }}
                  target="_blank"
                >
                  {row.original.request_for_invoicing_id}
                </Link>
              );
            }

            if (
              !row.original.invoice_no ||
              !row.original.billing_plan_invoice_id
            ) {
              return null;
            }

            return (
              <Link
                href={{
                  pathname:
                    row.original.type ===
                    BillingPlanBudgetItemInvoiceTypeEnum.OutgoingInvoice
                      ? "/invoices/outgoing-invoices/detail"
                      : row.original.type ===
                          BillingPlanBudgetItemInvoiceTypeEnum.OutgoingProformaInvoice
                        ? "/invoices/proforma-invoices/detail"
                        : "/invoices/for-invoicing/edit",
                  query: { id: row.original.billing_plan_invoice_id },
                }}
                target="_blank"
              >
                {row.original.invoice_no}
              </Link>
            );
          },
        },
        {
          id: "paid_at",
          meta: { label: t`Paid at`, align: "center" },
          header: t`Paid at`,
          cell: ({ row }) => {
            if (row.original.paid_at) {
              return formatDate(new Date(row.original.paid_at));
            }

            if (row.original.paid_amount && row.original.paid_amount !== 0) {
              return (
                <Text color="gray.500">
                  <Trans>Partially paid</Trans>
                </Text>
              );
            }

            return (
              <Text color="gray.500">
                <Trans>Unpaid</Trans>
              </Text>
            );
          },
        },
        {
          id: "status",
          meta: { label: t`Status` },
          header: t`Status`,
          orderBy: BillingPlanCacheOrderByColumnEnum.Status,
          cell: ({ row }) => {
            let colorScheme: BadgeProps["colorScheme"];
            let label: string;

            switch (row.original.status) {
              case BillingPlanBudgetCacheStatusEnum.Pending:
                colorScheme = "orange";
                label = t`Pending`;
                break;
              case BillingPlanBudgetCacheStatusEnum.Created:
                colorScheme = "green";
                label = t`Created`;
                break;
              case BillingPlanBudgetCacheStatusEnum.Failed:
                colorScheme = "red";
                label = t`Failed`;
                break;
              case BillingPlanBudgetCacheStatusEnum.Deleted:
                colorScheme = "red";
                label = t`Deleted`;
                break;
              case BillingPlanBudgetCacheStatusEnum.ManuallyCreated:
                colorScheme = "gray";
                label = t`Manually Created`;
                break;
              case BillingPlanBudgetCacheStatusEnum.Draft:
                colorScheme = "gray";
                label = t`Draft`;
                break;
              default:
                colorScheme = "gray";
                label = row.original.status;
                break;
            }

            return <Badge colorScheme={colorScheme}>{label}</Badge>;
          },
        },
        {
          id: "email-sent",
          meta: { label: t`Delivery` },
          header: t`Delivery`,
          cell: ({ row }) => (
            <DeliveredStatusRenderer delivered={row.original.email_sent} />
          ),
        },
        {
          id: "message",
          meta: { label: t`Message` },
          header: t`Message`,
          cell: ({ row }) => {
            if (!row.original.message) {
              return null;
            }

            return (
              <Text
                maxW="200px"
                color={
                  row.original.status ===
                  BillingPlanBudgetCacheStatusEnum.Failed
                    ? "red.500"
                    : "gray.600"
                }
                fontSize="sm"
                isTruncated
                title={row.original.message}
              >
                {row.original.message}
              </Text>
            );
          },
        },
        {
          id: ACTIONS_COLUMN_ID,
          meta: { label: t`Actions` },
          header: t`Actions`,
          cell: ({ row }) => {
            if (
              row.original.status === BillingPlanBudgetCacheStatusEnum.Failed
            ) {
              return (
                <Button
                  isLoading={store.rowCreateLoadingIds.has(row.original.id)}
                  onClick={() => store.createInvoiceForRow(row.original)}
                  size="xs"
                  variant="outline"
                >
                  <Trans>Create invoice</Trans>
                </Button>
              );
            }

            if (
              row.original.status === BillingPlanBudgetCacheStatusEnum.Draft &&
              row.original.request_for_invoicing_id
            ) {
              return (
                <ButtonLink
                  size="xs"
                  href={{
                    pathname: "/invoices/for-invoicing/edit",
                    query: {
                      id: row.original.request_for_invoicing_id,
                    },
                  }}
                  variant="outline"
                  target="_blank"
                >
                  <Trans>Create invoice</Trans>
                </ButtonLink>
              );
            }

            return null;
          },
        },
      ],
      [store],
    );

    return (
      <DataTable>
        <DataTable.Filter>
          <FiltersPanelRoot filters={store.where}>
            <VStack align="start" spacing="4">
              <HStack w="full" spacing="4">
                <InvoicingPlanLinks />

                <PeriodPicker
                  onChange={store.setDateRange}
                  value={store.dateRange}
                />

                <FiltersPanelSearch
                  onSearch={store.onSearch}
                  defaultValue={store.searchTerm}
                  width="full"
                  inputWidth="full"
                />

                <FiltersPanelFilters />
              </HStack>
            </VStack>
          </FiltersPanelRoot>
        </DataTable.Filter>

        <DataTable.SummaryBox hideBg>
          <DataTable.SummaryItem
            isLoaded={!store.isSummaryLoading}
            label={t`Projects`}
          >
            {store.summary?.projects ?? 0}
          </DataTable.SummaryItem>

          <DataTable.SummaryItem
            isLoaded={!store.isSummaryLoading}
            label={t`Planned`}
            alignment="right"
          >
            {currency.formatByWorkspace(store.summary?.planned_amount ?? 0)}
          </DataTable.SummaryItem>

          <DataTable.SummaryItem
            isLoaded={!store.isSummaryLoading}
            label={t`Invoiced`}
            alignment="right"
          >
            {currency.formatByWorkspace(store.summary?.invoiced_amount ?? 0)}
          </DataTable.SummaryItem>

          <DataTable.SummaryItem
            isLoaded={!store.isSummaryLoading}
            label={t`Balance`}
            alignment="right"
          >
            {currency.formatByWorkspace(store.summary?.uninvoiced_amount ?? 0)}
          </DataTable.SummaryItem>
        </DataTable.SummaryBox>

        <LoadingOverlay
          isLoading={store.isTableDataLoading}
          position="absolute"
        >
          <DataTable.Body
            tableKey={store.tableKey}
            columns={columns}
            data={store.tableData}
            orderedBy={store.orderBy.clauses?.[0]}
            onOrderByChange={store.handleChangeOrderBy}
          />
          <Pagination store={store.pagination} />
        </LoadingOverlay>
      </DataTable>
    );
  },
);
